"use client"

import React from "react"
import { useRef } from "react"
import {
  ChevronUp,
  ChevronDown,
  X,
  Zap,
  ShoppingBag,
  ArrowRight,
  ExternalLink,
  Link,
  Edit,
  Upload,
  RefreshCw,
  ChevronRight,
} from "lucide-react"
import Image from "next/image"

import type { Document as DocumentType } from "@/types"

interface LibraryDocumentPageProps {
  document?: DocumentType
  onExit?: () => void
  onGoToCart?: () => void
  onAddToCart?: () => void
  onCreateBorrowRequest?: () => void
}

export default function LibraryDocumentPage({ 
  document,
  onExit, 
  onGoToCart, 
  onAddToCart, 
  onCreateBorrowRequest 
}: LibraryDocumentPageProps) {
  // Refs for each section
  const topRef = useRef<HTMLDivElement>(null)
  const borrowRef = useRef<HTMLDivElement>(null)
  const locationRef = useRef<HTMLDivElement>(null)
  const onlineRef = useRef<HTMLDivElement>(null)
  const infoRef = useRef<HTMLDivElement>(null)
  const statusRef = useRef<HTMLDivElement>(null)
  const historyRef = useRef<HTMLDivElement>(null)
  const conservationRef = useRef<HTMLDivElement>(null)
  const scrollToSection = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      const headerHeight = 100 // Height of sticky header + some padding
      const elementPosition = ref.current.offsetTop - headerHeight
      
      window.scrollTo({
        top: Math.max(0, elementPosition), // Ensure we don't scroll to negative position
        behavior: "smooth"
      })
    }
  }

  const scrollToBottom = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({
        top: window.document.documentElement.scrollHeight,
        behavior: "smooth"
      })
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Sidebar - Narrower */}
      <div className="w-20 bg-[#a5d6a7] flex flex-col fixed h-full z-10">
        <div className="flex flex-col items-center py-4 space-y-6">
          <button
            className="w-full py-3 flex justify-center hover:bg-[#8bc34a]/20"
            onClick={() => scrollToSection(topRef)}
          >
            <ChevronUp className="h-6 w-6" />
          </button>          <button 
            className="w-full py-3 flex justify-center hover:bg-[#8bc34a]/20"
            onClick={onExit}
          >
            <X className="h-6 w-6" />
          </button>
          <button 
            className="w-full py-3 flex justify-center hover:bg-[#8bc34a]/20"
            onClick={scrollToBottom}
          >
            <ChevronDown className="h-6 w-6" />
          </button>
        </div>

        <div className="mt-10 flex flex-col text-xs">
          <button
            className="w-full text-center px-2 py-4 hover:bg-[#8bc34a]/20 leading-tight"
            onClick={() => scrollToSection(topRef)}
          >
            LÊN ĐẦU
          </button>
          <button
            className="w-full text-center px-2 py-4 hover:bg-[#8bc34a]/20 leading-tight"
            onClick={() => scrollToSection(borrowRef)}
          >
            MƯỢN TÀI LIỆU
          </button>
          <button
            className="w-full text-center px-2 py-4 hover:bg-[#8bc34a]/20 leading-tight"
            onClick={() => scrollToSection(locationRef)}
          >
            VỊ TRÍ
          </button>
          <button
            className="w-full text-center px-2 py-4 hover:bg-[#8bc34a]/20 leading-tight"
            onClick={() => scrollToSection(onlineRef)}
          >
            XEM ONLINE
          </button>
          <button
            className="w-full text-center px-2 py-4 hover:bg-[#8bc34a]/20 leading-tight"
            onClick={() => scrollToSection(infoRef)}
          >
            THÔNG TIN
          </button>
          <button
            className="w-full text-center px-2 py-4 hover:bg-[#8bc34a]/20 text-red-700 leading-tight"
            onClick={() => scrollToSection(statusRef)}
          >
            TRẠNG THÁI MƯỢN
          </button>
          <button
            className="w-full text-center px-2 py-4 hover:bg-[#8bc34a]/20 text-red-700 leading-tight"
            onClick={() => scrollToSection(historyRef)}
          >
            LỊCH SỬ MƯỢN TRẢ
          </button>
          <button
            className="w-full text-center px-2 py-4 hover:bg-[#8bc34a]/20 text-red-700 leading-tight"
            onClick={() => scrollToSection(conservationRef)}
          >
            TÌNH TRẠNG BẢO QUẢN
          </button>
        </div>
      </div>

      {/* Main Content - Offset by sidebar width */}
      <div className="flex-1 ml-20">        {/* Header */}
        <header className="bg-white shadow-sm py-4 px-6 flex justify-between items-center sticky top-0 z-5">
          <h1 className="text-2xl font-bold">THƯ VIỆN TÀI LIỆU/HIỆN VẬT</h1>
          <button 
            className="flex items-center space-x-2 border border-gray-300 rounded-md px-4 py-2 hover:bg-gray-50"
            onClick={onGoToCart}
          >
            <ShoppingBag className="h-5 w-5" />
            <span>Giỏ mượn</span>
          </button>
        </header>        {/* All Content Sections */}
        <div className="overflow-y-auto h-screen">
          {/* Top Section */}
          <div ref={topRef} className="p-6">
            <div className="flex space-x-6 mb-10">              <div className="w-64 h-64 bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden">
                <div className="relative w-full h-full">
                  <Image
                    src={document?.coverImage || "/images/placeholder-664.png"}
                    alt={document?.title || "Kỷ yếu giải thưởng văn học nghệ thuật"}
                    fill
                    style={{ objectFit: "contain" }}
                    className="p-2"
                  />
                </div>
              </div>
              <div className="flex-1">
                <div className="text-sm text-gray-500 mb-1">{document?.type?.toUpperCase() || "SÁCH"}</div>
                <h2 className="text-2xl font-medium text-[#4caf50] mb-2">
                  {document?.title || "Kỷ yếu giải thưởng văn học nghệ thuật 1970 của Tổng thống VNCH"}
                </h2>
                <div className="text-gray-700 mb-1">{document?.authors?.join(", ") || "N/A"}</div>
                <div className="text-gray-700">{document?.year || "N/A"}</div>
              </div>
            </div>
          </div>          {/* Borrow Section */}
          <div ref={borrowRef} className="p-6 border-t border-gray-200">
            <h3 className="text-2xl font-medium border-b border-gray-200 pb-2 mb-4">MƯỢN TÀI LIỆU</h3>
            <div className="flex space-x-4">
              <button 
                className="flex items-center space-x-2 bg-blue-100 hover:bg-blue-200 text-blue-700 px-6 py-3 rounded-md transition-colors"
                onClick={onCreateBorrowRequest}
              >
                <Zap className="h-5 w-5" />
                <span>Tạo phiếu mượn nhanh</span>
              </button>
              <button 
                className="flex items-center space-x-2 bg-green-100 hover:bg-green-200 text-green-700 px-6 py-3 rounded-md transition-colors"
                onClick={onAddToCart}
              >
                <ShoppingBag className="h-5 w-5" />
                <span>Thêm vào giỏ mượn</span>
              </button>
            </div>
          </div>

          {/* Location Section */}
          <div ref={locationRef} className="p-6 border-t border-gray-200">
            <h3 className="text-2xl font-medium border-b border-gray-200 pb-2 mb-4">VỊ TRÍ</h3>
            <div className="mb-2">
              <div className="text-xl">Thư viện Dinh Độc Lập</div>
              <div className="text-gray-700">Trong kho, Phòng 1, Kệ 1, Mặt kệ 1, Hàng 3, Ô 1</div>
            </div>
            <button className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-md">
              <ArrowRight className="h-5 w-5" />
              <span>Xem trong q.lý giá kệ</span>
            </button>
          </div>

          {/* Online Section */}
          <div ref={onlineRef} className="p-6 border-t border-gray-200">
            <h3 className="text-2xl font-medium border-b border-gray-200 pb-2 mb-6">XEM ONLINE</h3>
            <div className="flex space-x-4 mb-8">
              <button className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-md">
                <ExternalLink className="h-5 w-5 text-blue-600" />
                <span className="text-blue-600">Đọc Online / Xem hiện vật 3D</span>
              </button>
              <button className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-md">
                <Link className="h-5 w-5" />
                <span>Sao chép đường dẫn</span>
              </button>
            </div>
          </div>

          {/* Info Section */}
          <div ref={infoRef} className="p-6 border-t border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-medium border-b border-gray-200 pb-2">THÔNG TIN</h3>
              <Edit className="h-5 w-5 text-gray-500" />
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Tiêu đề:</div>
                <div className="col-span-2">{document?.title || "N/A"}</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Tác giả:</div>
                <div className="col-span-2">{document?.authors?.join(", ") || "N/A"}</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Chủ đề:</div>
                <div className="col-span-2 space-y-1">
                  {document?.subjects?.map((sub, idx) => (
                    <div key={idx} className="text-green-600 flex items-center">
                      {sub} {idx < (document.subjects?.length || 0) - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Thể loại:</div>
                <div className="col-span-2">{document?.type || "N/A"}</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Mô tả:</div>
                <div className="col-span-2">{document?.description || "Không có mô tả"}</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Năm xuất bản:</div>
                <div className="col-span-2">{document?.year || "N/A"}</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Nguồn:</div>
                <div className="col-span-2">Nội bộ</div>
              </div>
            </div>
          </div>

          {/* Status Section */}
          <div ref={statusRef} className="p-6 border-t border-gray-200">
            <h3 className="text-2xl font-medium text-red-600 border-b border-gray-200 pb-2 mb-6">TRẠNG THÁI MƯỢN</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Trạng thái:</div>
                <div className="col-span-2">Đã trả</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Người làm phiếu mượn:</div>
                <div className="col-span-2 text-green-600">Đỗ Nhật Minh</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Người duyệt:</div>
                <div className="col-span-2 text-green-600">Trần Duy Hào</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Ngày nhận tài liệu:</div>
                <div className="col-span-2">4/7/2024</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Người bàn giao:</div>
                <div className="col-span-2 text-green-600">Cao Phúc Thành</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Ngày hẹn trả tài liệu:</div>
                <div className="col-span-2">8/7/2024</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Người tiếp nhận kiểm tra:</div>
                <div className="col-span-2 text-green-600">Trần Thanh Ngọc</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Số ngày quá hạn:</div>
                <div className="col-span-2">Chưa áp dụng</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="font-medium">Cá nhân/Đơn vị quản lý:</div>
                <div className="col-span-2 text-green-600">Trần Duy Hào</div>
              </div>
            </div>
          </div>

          {/* History Section */}
          <div ref={historyRef} className="p-6 border-t border-gray-200">
            <h3 className="text-2xl font-medium text-red-600 border-b border-gray-200 pb-2 mb-6">LỊCH SỬ MƯỢN TRẢ</h3>
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-700">Đỗ Nhật Minh</div>
                <div className="text-gray-600">4/7/2024- 6/7/2024</div>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-700">Phan Văn Mạnh</div>
                <div className="text-gray-600">1/7/2024- 3/7/2024</div>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-700">Trần Đại Nam</div>
                <div className="text-gray-600">28/6/2024- 30/6/2024</div>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-700">Dương Việt Dũng</div>
                <div className="text-gray-600">21/6/2024- 27/6/2024</div>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-700">Ánh Dương</div>
                <div className="text-gray-600">17/6/2024- 20/6/2024</div>
              </div>
            </div>
          </div>

          {/* Conservation Section */}
          <div ref={conservationRef} className="p-6 border-t border-gray-200">
            <h3 className="text-2xl font-medium text-red-600 border-b border-gray-200 pb-2 mb-6">
              TÌNH TRẠNG BẢO QUẢN
            </h3>

            {/* Latest Status Update */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">CẬP NHẬT TÌNH TRẠNG (NẾU CẦN)</h4>
                <ChevronUp className="h-4 w-4 text-gray-500" />
              </div>
              <div className="text-sm text-gray-500 mb-4">
                Cao Phúc Thành 10/7/2024 5:25 PM (Phiếu bàn giao: ID00023974)
              </div>

              <div className="mb-4">
                <div className="text-sm text-gray-600 mb-2">Upload tài liệu đính kèm (nếu có):</div>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <div className="text-gray-600">Upload file (.pdf, .jpg, .png, .mp4)</div>
                </div>
              </div>

              <button className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg flex items-center justify-center space-x-2">
                <RefreshCw className="h-4 w-4" />
                <span>Cập nhật tình trạng</span>
              </button>
            </div>

            {/* History */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-gray-600 mb-4">LỊCH SỬ CẬP NHẬT</h4>

              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500 mb-2">
                    Cao Phúc Thành 10/7/2024 15:45 (Phiếu nhận: ID000198)
                  </div>
                  <div className="font-medium mb-2">Sách bị ướt góc trái</div>                  <div className="w-16 h-16 bg-gray-100 border border-gray-200 rounded overflow-hidden">
                    <Image
                      src="/images/background.png"
                      alt="Damaged book corner"
                      width={64}
                      height={64}
                      className="object-cover"
                    />
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-500 mb-2">Cao Phúc Thành 3/6/2024 15:45 (Phiếu nhận: ID000198)</div>
                  <div className="font-medium">Trang 36 có vết mực viết tay</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
